import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Link,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { getGroupDetail, getGroupAdmins, createGroupAdmin, updateGroupAdmin, deleteGroupAdmin } from '../../services/api';
import ConfirmDeleteDialog from '../../components/Common/ConfirmDeleteDialog';
import Loader from '../../components/Common/Loader';
import { useAuth } from '../../context/AuthContext';

// New group admin logins start on this PIN until the admin changes it (on
// first login in the app, or manually here) - show it as the real default
// rather than leaving the field blank.
const DEFAULT_PIN = '8664';

const emptyNewRow = () => ({ phone: '', pin: DEFAULT_PIN, wikiwaste_email: '' });
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const onlyDigits = (value) => value.replace(/\D/g, '');

const validateDraft = (draft) => {
  const errors = {};
  if (!draft.phone.trim()) {
    errors.phone = 'Log in ID is required';
  } else if (!/^\d+$/.test(draft.phone.trim())) {
    errors.phone = 'Log in ID must be numbers only';
  }
  if (draft.pin && draft.pin.length !== 4) {
    errors.pin = 'PIN must be 4 digits';
  }
  if (draft.wikiwaste_email && !emailRegex.test(draft.wikiwaste_email.trim())) {
    errors.wikiwaste_email = 'Invalid email address';
  }
  return errors;
};

const GroupAdministrators = () => {
  const { groupId } = useParams();
  const { user } = useAuth();
  const canDelete = user?.userType !== 'sub_user';

  const [group, setGroup] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newRow, setNewRow] = useState(emptyNewRow());
  const [newRowErrors, setNewRowErrors] = useState({});

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyNewRow());
  const [editRowErrors, setEditRowErrors] = useState({});

  const [notesRow, setNotesRow] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesSynced, setNotesSynced] = useState(''); // last value we know came from the server

  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [detailRes, adminsRes] = await Promise.all([
        getGroupDetail(groupId).catch(() => null),
        getGroupAdmins(groupId)
      ]);
      if (detailRes?.success) setGroup(detailRes.data);
      if (adminsRes.success) setRows(adminsRes.data);
    } catch (err) {
      if (!silent) setError(err.response?.data?.error || 'Failed to load group administrators');
      else console.error('Background group admins refresh failed:', err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [groupId]);

  // Logins can be added/edited from elsewhere — poll quietly in the background so
  // this list stays live without a manual refresh. Kept running even while a row is
  // being edited or Notes is open: editDraft/notesDraft are held in their own state,
  // not derived from `rows`, so this can't clobber in-progress typing — see the
  // effect below for how the open Notes popup itself picks up an external change.
  useEffect(() => {
    const interval = setInterval(() => {
      if (!localStorage.getItem('token')) return;
      load(true);
    }, 15000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    const errors = validateDraft(newRow);
    setNewRowErrors(errors);
    if (Object.keys(errors).length > 0) return;
    try {
      const res = await createGroupAdmin(groupId, newRow);
      setRows(prev => [...prev, {
        id: res.data.id,
        phone: newRow.phone,
        pin: newRow.pin,
        wikiwaste_email: newRow.wikiwaste_email,
        notes: null
      }]);
      setNewRow(emptyNewRow());
      setNewRowErrors({});
      setSuccess('Login added');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add login');
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditDraft({ phone: row.phone || '', pin: row.pin || DEFAULT_PIN, wikiwaste_email: row.wikiwaste_email || '' });
    setEditRowErrors({});
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditRowErrors({});
  };

  const saveEdit = async (row) => {
    const errors = validateDraft(editDraft);
    setEditRowErrors(errors);
    if (Object.keys(errors).length > 0) return;
    try {
      await updateGroupAdmin(groupId, row.id, editDraft);
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, ...editDraft } : r));
      setEditingId(null);
      setEditRowErrors({});
      setSuccess('Login updated');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update login');
    }
  };

  const requestRemove = (row) => setConfirmDelete(row);

  const handleConfirmDelete = async () => {
    const row = confirmDelete;
    setConfirmDelete(null);
    try {
      await deleteGroupAdmin(groupId, row.id);
      setRows(prev => prev.filter(r => r.id !== row.id));
      setEditingId(null);
      setSuccess('Login removed');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove login');
    }
  };

  const openNotes = (row) => {
    setNotesRow(row);
    setNotesDraft(row.notes || '');
    setNotesSynced(row.notes || '');
  };

  const saveNotes = async () => {
    try {
      await updateGroupAdmin(groupId, notesRow.id, { notes: notesDraft });
      setRows(prev => prev.map(r => r.id === notesRow.id ? { ...r, notes: notesDraft } : r));
      setNotesRow(null);
      setSuccess('Notes saved');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save notes');
    }
  };

  // While the Notes popup is open, the background poll above keeps refreshing
  // `rows` underneath it — pick up a value changed elsewhere and reflect it live.
  // Only auto-applies if the textbox still shows what we last synced, i.e. the
  // admin hasn't started typing their own edit — that always wins.
  useEffect(() => {
    if (!notesRow) return;
    const fresh = rows.find((r) => r.id === notesRow.id);
    if (!fresh) return;
    const freshNotes = fresh.notes || '';
    if (freshNotes !== notesSynced) {
      if (notesDraft === notesSynced) setNotesDraft(freshNotes);
      setNotesSynced(freshNotes);
      setNotesRow(fresh);
    }
  }, [rows, notesRow, notesDraft, notesSynced]);

  const colWidths = { phone: 220, pin: 100, email: 260 };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1 }}>
        <Typography variant="h4" fontWeight={600}>Group Administrators</Typography>
        {!loading && (
          <Typography variant="h6" color="text.secondary" fontWeight={400}>
            ({rows.length})
          </Typography>
        )}
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {group ? `${group.name_english}${group.country_name ? ` — ${group.country_name}` : ''}` : `Group ID: ${groupId}`}
        </Typography>

        {loading ? (
          <Loader />
        ) : (
          <>
            {/* Column headers */}
            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ width: colWidths.phone }}>Log in ID</Typography>
              <Typography variant="subtitle2" sx={{ width: colWidths.pin }}>PIN</Typography>
              <Typography variant="subtitle2" sx={{ width: colWidths.email }}>Wikiwaste Email</Typography>
            </Box>

            {/* New row */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <TextField
                size="small"
                placeholder="Log in ID"
                sx={{ width: colWidths.phone }}
                value={newRow.phone}
                onChange={(e) => { setNewRow({ ...newRow, phone: onlyDigits(e.target.value) }); setNewRowErrors({ ...newRowErrors, phone: undefined }); }}
                error={!!newRowErrors.phone}
                helperText={newRowErrors.phone || ''}
                inputProps={{ inputMode: 'numeric' }}
              />
              <TextField
                size="small"
                placeholder="PIN"
                sx={{ width: colWidths.pin }}
                value={newRow.pin}
                onChange={(e) => { setNewRow({ ...newRow, pin: onlyDigits(e.target.value).slice(0, 4) }); setNewRowErrors({ ...newRowErrors, pin: undefined }); }}
                error={!!newRowErrors.pin}
                helperText={newRowErrors.pin || ''}
                inputProps={{ inputMode: 'numeric', maxLength: 4 }}
              />
              <TextField
                size="small"
                placeholder="Email"
                sx={{ width: colWidths.email }}
                value={newRow.wikiwaste_email}
                onChange={(e) => { setNewRow({ ...newRow, wikiwaste_email: e.target.value }); setNewRowErrors({ ...newRowErrors, wikiwaste_email: undefined }); }}
                error={!!newRowErrors.wikiwaste_email}
                helperText={newRowErrors.wikiwaste_email || ''}
              />
              <Link component="button" underline="hover" onClick={handleCreate} sx={{ mt: 1 }}>New</Link>
            </Box>

            {/* Existing rows */}
            {rows.map((row) => {
              const isEditing = editingId === row.id;
              return (
                <Box key={row.id} sx={{ display: 'flex', alignItems: isEditing ? 'flex-start' : 'center', gap: 2, py: 1, borderBottom: '1px solid #f0f0f0' }}>
                  {isEditing ? (
                    <>
                      <TextField
                        size="small"
                        sx={{ width: colWidths.phone }}
                        value={editDraft.phone}
                        onChange={(e) => { setEditDraft({ ...editDraft, phone: onlyDigits(e.target.value) }); setEditRowErrors({ ...editRowErrors, phone: undefined }); }}
                        error={!!editRowErrors.phone}
                        helperText={editRowErrors.phone || ''}
                        inputProps={{ inputMode: 'numeric' }}
                      />
                      <TextField
                        size="small"
                        sx={{ width: colWidths.pin }}
                        value={editDraft.pin}
                        onChange={(e) => { setEditDraft({ ...editDraft, pin: onlyDigits(e.target.value).slice(0, 4) }); setEditRowErrors({ ...editRowErrors, pin: undefined }); }}
                        error={!!editRowErrors.pin}
                        helperText={editRowErrors.pin || ''}
                        inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                      />
                      <TextField
                        size="small"
                        sx={{ width: colWidths.email }}
                        value={editDraft.wikiwaste_email}
                        onChange={(e) => { setEditDraft({ ...editDraft, wikiwaste_email: e.target.value }); setEditRowErrors({ ...editRowErrors, wikiwaste_email: undefined }); }}
                        error={!!editRowErrors.wikiwaste_email}
                        helperText={editRowErrors.wikiwaste_email || ''}
                      />
                      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        <Link component="button" underline="hover" onClick={() => saveEdit(row)}>Save</Link>
                        <Link component="button" underline="hover" onClick={cancelEdit}>Cancel</Link>
                        {canDelete && (
                          <Link component="button" underline="hover" color="error" onClick={() => requestRemove(row)}>Remove</Link>
                        )}
                      </Box>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ width: colWidths.phone }}>{row.phone}</Typography>
                      <Typography variant="body2" sx={{ width: colWidths.pin }}>{row.pin || DEFAULT_PIN}</Typography>
                      <Typography variant="body2" sx={{ width: colWidths.email }}>{row.wikiwaste_email || '-'}</Typography>
                      <Link component="button" underline="hover" onClick={() => startEdit(row)}>Edit</Link>
                      <Link component="button" underline="hover" onClick={() => openNotes(row)}>
                        Notes{row.notes ? ' •' : ''}
                      </Link>
                    </>
                  )}
                </Box>
              );
            })}
          </>
        )}
      </Paper>

      {/* Notes popup */}
      <Dialog open={!!notesRow} onClose={() => setNotesRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Notes — {notesRow?.phone}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            minRows={4}
            fullWidth
            placeholder="Add notes about this login..."
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesRow(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveNotes}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDeleteDialog
        open={!!confirmDelete}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete(null)}
      />

      <Snackbar
        open={!!success || !!error}
        autoHideDuration={5000}
        onClose={() => { setSuccess(''); setError(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => { setSuccess(''); setError(''); }}
          severity={success ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {success || error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GroupAdministrators;
